/**
 * Themed OBS overlay — renders all overlay elements using the active theme.
 * Used by both the real OBS overlay page and the Remotion demo videos.
 */

import type { OverlayTheme } from "./styles";

interface Rider {
  id: string;
  name: string;
  initials: string;
  colour: string;
  score: number | null;
  hits: number;
  nickname?: string | null;
  number?: string | null;
  funFact?: string | null;
  hasPhoto?: boolean;
  currentAttempt?: number;
  maxAttempts?: number;
  attemptScores?: Record<string, number | null>;
}

interface ThemedOverlayProps {
  theme: OverlayTheme;
  heatName: string;
  competitionName: string;
  riders: Rider[];
  focusRider?: Rider | null;
  scorecardRider?: Rider | null;
  scorecardJudges?: Array<{ name: string; value: number }>;
  scorecardFinal?: number | null;
  scorecardModel?: string;
  showLeaderboard?: boolean;
  showHeatScores?: boolean;
  showRiderScores?: boolean;
  showHeatBanner?: boolean;
  judgeNames?: string[];
  judgeCount?: number;
  allScores?: Record<string, Record<string, number>>;
  isFreeTier?: boolean;
  /** Per-attempt per-judge scores for the scorecard grid: {attempt: {judge: score}} */
  scorecardAttemptScores?: Record<string, Record<string, number>>;
  /** Per-attempt final scores for the scorecard grid: {attempt: final} */
  scorecardAttemptFinals?: Record<string, number | null>;
  /** Max attempts for the scorecard grid */
  scorecardMaxAttempts?: number;
}

export function ThemedOverlay({
  theme: t,
  heatName,
  competitionName,
  riders,
  focusRider,
  scorecardRider,
  scorecardJudges,
  scorecardFinal,
  scorecardModel,
  showLeaderboard,
  showHeatScores,
  showRiderScores,
  showHeatBanner = true,
  judgeNames,
  judgeCount = 5,
  allScores,
  isFreeTier = false,
  scorecardAttemptScores,
  scorecardAttemptFinals,
  scorecardMaxAttempts = 1,
}: ThemedOverlayProps) {
  return (
    <div style={{ width: 1920, height: 1080, position: "relative", fontFamily: "'Inter', sans-serif" }}>
      {/* ── Heat Banner (top-left) ── */}
      {showHeatBanner && heatName && (
        <div style={{
          position: "absolute", top: 30, left: 40,
          display: "flex", alignItems: "center", gap: 12,
          background: t.banner.bg, border: `1px solid ${t.banner.border}`,
          padding: "10px 24px", backdropFilter: t.blur,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.banner.liveColor, boxShadow: `0 0 8px ${t.banner.liveColor}` }} />
          <span style={{ fontSize: 18, fontWeight: 800, color: t.banner.text, textTransform: "uppercase", letterSpacing: 1 }}>{heatName}</span>
          <span style={{ fontSize: 12, color: t.banner.subtext, fontFamily: "'JetBrains Mono', monospace" }}>{competitionName}</span>
        </div>
      )}

      {/* ── Free Tier Watermark (top-right) ── */}
      {isFreeTier && (
        <div style={{
          position: "absolute", top: 30, right: 40,
          padding: "6px 16px",
          background: t.banner.bg, border: `1px solid ${t.banner.border}`,
          backdropFilter: t.blur,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: t.banner.subtext, letterSpacing: 0.5 }}>
            Scores by <span style={{ fontWeight: 800, color: t.banner.text }}>RALEY</span>
          </span>
        </div>
      )}

      {/* ── Rider Bar (bottom) — hidden when leaderboard is showing ── */}
      {showHeatScores && !showLeaderboard && <div style={{ position: "absolute", bottom: 40, left: 40, right: 40, display: "flex", gap: 10, opacity: 0.85 }}>
        {riders.map((r) => {
          const maxAttempts = r.maxAttempts ?? 1;
          const attemptScores = r.attemptScores ?? {};
          // Find highest attempt score for highlighting
          let highestAttempt: number | null = null;
          let highestVal = -1;
          for (let a = 1; a <= maxAttempts; a++) {
            const val = attemptScores[String(a)];
            if (val != null && val > highestVal) {
              highestVal = val;
              highestAttempt = a;
            }
          }

          return (
          <div key={r.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            background: t.chip.bg, border: `1px solid ${t.chip.border}`,
            padding: "8px 14px", backdropFilter: t.blur,
          }}>
            {r.hasPhoto ? (
              <img
                src={`${import.meta.env.VITE_API_URL ?? ""}/api/riders/${r.id}/photo`}
                style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: `2px solid ${t.chip.avatarBorder}` }}
              />
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: r.colour, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 700, color: "#fff", border: `2px solid ${t.chip.avatarBorder}`,
              }}>
                {r.initials}
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: t.chip.nameColor }}>{r.name}</div>
              {r.nickname && <div style={{ fontSize: 10, color: t.chip.scoreColor, marginTop: -1 }}>{r.nickname}</div>}
              {maxAttempts > 1 && (
                <div style={{ display: "flex", gap: 4, marginTop: 3, alignItems: "center" }}>
                  {Array.from({ length: maxAttempts }, (_, i) => {
                    const done = i + 1 <= (r.currentAttempt ?? 0);
                    return (
                      <div key={i} style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: done ? "#3b82f6" : "transparent",
                        border: `1.5px solid ${done ? "#3b82f6" : "rgba(255,255,255,0.25)"}`,
                        boxShadow: done ? "0 0 4px rgba(59,130,246,0.4)" : "none",
                      }} />
                    );
                  })}
                </div>
              )}
              {showRiderScores && maxAttempts > 1 && (
                <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                  {Array.from({ length: maxAttempts }, (_, i) => {
                    const attemptNum = i + 1;
                    const val = attemptScores[String(attemptNum)];
                    const isHighest = attemptNum === highestAttempt;
                    const happened = attemptNum <= (r.currentAttempt ?? 0);
                    return (
                      <div key={attemptNum} style={{
                        minWidth: 28, height: 20, borderRadius: 3,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700,
                        fontFamily: "'JetBrains Mono', monospace",
                        background: isHighest ? "rgba(59,130,246,0.3)" : "rgba(255,255,255,0.05)",
                        border: `1px solid ${isHighest ? "rgba(59,130,246,0.6)" : "rgba(255,255,255,0.1)"}`,
                        color: isHighest ? "#60a5fa" : val != null ? t.chip.scoreColor : "rgba(255,255,255,0.3)",
                      }}>
                        {val != null ? val.toFixed(1) : happened ? "-" : ""}
                      </div>
                    );
                  })}
                </div>
              )}
              {showRiderScores && maxAttempts <= 1 && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
                  <span style={{ fontSize: 11, color: t.chip.scoreColor, fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.score != null ? r.score.toFixed(1) : "-"}
                  </span>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>}

      {/* ── Focus Card (left) ── */}
      {focusRider && (
        <div style={{
          position: "absolute", left: 60, top: "40%", transform: "translateY(-50%)",
          background: t.focus.bg, border: `1px solid ${t.focus.border}`,
          padding: 28, backdropFilter: t.blur, minWidth: 280,
        }}>
          {focusRider.hasPhoto ? (
            <img
              src={`${import.meta.env.VITE_API_URL ?? ""}/api/riders/${focusRider.id}/photo`}
              style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", marginBottom: 14 }}
            />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: "50%", background: focusRider.colour,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 14,
            }}>
              {focusRider.initials}
            </div>
          )}
          <div style={{ fontSize: 24, fontWeight: 800, color: t.focus.nameColor }}>{focusRider.name}</div>
          {focusRider.nickname && <div style={{ fontSize: 14, color: t.focus.nickColor, marginTop: 4 }}>&ldquo;{focusRider.nickname}&rdquo;</div>}
          {focusRider.number && <div style={{ fontSize: 13, color: t.focus.metaColor, fontFamily: "'JetBrains Mono', monospace", marginTop: 6 }}>#{focusRider.number}</div>}
          {focusRider.funFact && <div style={{ fontSize: 12, color: t.focus.factColor, marginTop: 10, fontStyle: "italic", maxWidth: 240 }}>{focusRider.funFact}</div>}
        </div>
      )}

      {/* ── Scorecard Grid (center) — judges as rows, attempts as columns ── */}
      {scorecardRider && scorecardJudges && (() => {
        const maxAtt = scorecardMaxAttempts;
        const attScores = scorecardAttemptScores ?? {};
        const attFinals = scorecardAttemptFinals ?? {};

        // Find the best attempt final
        let bestAttempt: string | null = null;
        let bestVal = -1;
        for (let a = 1; a <= maxAtt; a++) {
          const val = attFinals[String(a)];
          if (val != null && val > bestVal) {
            bestVal = val;
            bestAttempt = String(a);
          }
        }

        return (
          <div style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            background: t.scorecard.bg, border: `1px solid ${t.scorecard.border}`,
            padding: "32px 40px", backdropFilter: t.blur,
            opacity: 0.85, minWidth: maxAtt > 1 ? 400 : 320,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.scorecard.nameColor, marginBottom: 20, textAlign: "center" }}>{scorecardRider.name}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, color: t.scorecard.judgeLabelColor, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid ${t.scorecard.divider}` }}></th>
                  {Array.from({ length: maxAtt }, (_, i) => (
                    <th key={i} style={{ padding: "6px 8px", textAlign: "center", fontSize: 9, color: t.scorecard.judgeLabelColor, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", borderBottom: `1px solid ${t.scorecard.divider}` }}>
                      A{i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: judgeCount }, (_, j) => {
                  const judgeNum = String(j + 1);
                  return (
                    <tr key={j}>
                      <td style={{ padding: "5px 8px", fontSize: 10, color: t.scorecard.judgeLabelColor, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                        J{judgeNum}
                      </td>
                      {Array.from({ length: maxAtt }, (_, a) => {
                        const attemptNum = String(a + 1);
                        const val = attScores[attemptNum]?.[judgeNum];
                        return (
                          <td key={a} style={{ padding: "5px 8px", textAlign: "center", fontSize: 16, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: val != null ? t.scorecard.scoreColor : t.scorecard.judgeLabelColor }}>
                            {val != null ? val.toFixed(1) : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                <tr style={{ borderTop: `1px solid ${t.scorecard.divider}` }}>
                  <td style={{ padding: "6px 8px", fontSize: 10, fontWeight: 700, color: t.scorecard.judgeLabelColor, fontFamily: "'JetBrains Mono', monospace" }}>
                    Final
                  </td>
                  {Array.from({ length: maxAtt }, (_, a) => {
                    const attemptNum = String(a + 1);
                    const val = attFinals[attemptNum];
                    const isBest = attemptNum === bestAttempt;
                    return (
                      <td key={a} style={{ padding: "6px 8px", textAlign: "center", fontSize: 18, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: isBest ? t.scorecard.finalColor : t.scorecard.scoreColor }}>
                        {val != null ? val.toFixed(1) : "-"}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
            <div style={{ borderTop: `1px solid ${t.scorecard.divider}`, paddingTop: 14, marginTop: 8, textAlign: "center" }}>
              <div style={{ fontSize: 9, color: t.scorecard.finalLabelColor, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>
                Overall{scorecardModel ? ` (${scorecardModel})` : ""}
              </div>
              <div style={{ fontSize: 44, fontWeight: 800, color: t.scorecard.finalColor }}>
                {scorecardFinal != null ? scorecardFinal.toFixed(1) : "-"}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Heat Leaderboard (center) ── */}
      {showLeaderboard && (
        <div style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          background: t.leaderboard.bg, border: `1px solid ${t.leaderboard.border}`,
          padding: "28px 36px", backdropFilter: t.blur, minWidth: 650, maxWidth: "85%",
          opacity: 0.85,
        }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: t.leaderboard.titleColor, textTransform: "uppercase", letterSpacing: 1, marginBottom: 20 }}>{heatName}</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: t.leaderboard.headerBg }}>
                <th style={thStyle(t.leaderboard.headerColor)}>#</th>
                <th style={{ ...thStyle(t.leaderboard.headerColor), textAlign: "left" }}>Rider</th>
                {(judgeNames ?? Array.from({ length: judgeCount }, (_, i) => `J${i + 1}`)).map((j) => (
                  <th key={j} style={thStyle(t.leaderboard.headerColor)}>{j}</th>
                ))}
                <th style={thStyle(t.leaderboard.headerColor)}>Final</th>
              </tr>
            </thead>
            <tbody>
              {[...riders].sort((a, b) => (b.score ?? -1) - (a.score ?? -1)).map((r, i) => {
                const riderScores = allScores?.[r.id] ?? {};
                const scoreValues = Object.values(riderScores);
                return (
                <tr key={r.id} style={{ background: i % 2 === 0 ? t.leaderboard.rowBg : t.leaderboard.rowAltBg }}>
                  <td style={{ ...tdStyle(t.leaderboard.rowBorder), color: t.leaderboard.scoreColor, textAlign: "center" }}>{i + 1}</td>
                  <td style={{ ...tdStyle(t.leaderboard.rowBorder), color: t.leaderboard.nameColor, fontWeight: 600 }}>{r.name}</td>
                  {Array.from({ length: judgeCount }, (_, j) => (
                    <td key={j} style={{ ...tdStyle(t.leaderboard.rowBorder), color: t.leaderboard.scoreColor, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                      {scoreValues[j] != null ? scoreValues[j].toFixed(1) : "-"}
                    </td>
                  ))}
                  <td style={{
                    ...tdStyle(t.leaderboard.rowBorder),
                    textAlign: "center", fontWeight: 800, fontSize: 16,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: i === 0 ? t.leaderboard.firstPlaceColor : t.leaderboard.finalColor,
                  }}>
                    {r.score != null ? r.score.toFixed(1) : "-"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}


const thStyle = (color: string): React.CSSProperties => ({
  padding: "8px 10px", color, fontSize: 9, textTransform: "uppercase",
  fontFamily: "'JetBrains Mono', monospace", textAlign: "center",
  borderBottom: "1px solid rgba(128,128,128,0.1)",
});

const tdStyle = (borderColor: string): React.CSSProperties => ({
  padding: "8px 10px", borderBottom: `1px solid ${borderColor}`, fontSize: 13,
});
